import { get, map as _map } from 'lodash';

// Modified by AI on 06/30/2026. Edit #1.
import React, {
    memo, useState, Fragment, useEffect, useContext, useRef, useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from '@material-ui/core/Link';
import Highlighter from 'react-highlight-words';
import useStyles from './CandidatesSearchComponentStyles';
import SuggestionOptions from '../../common/tags-input/SuggestionOptions';
import Loader from '../../common/loader/LoaderComponent';
import {
    darkslategray,
    lightslategray,
    saffronMango,
} from '../../../constants/Colors';
import EmployerHttpService from '../../../api/EmployerHttpService';
import EmployerContext from '../../../contexts/EmployerContext';
import NotificationContext from '../../../contexts/NotificationContext';
import CandidateProfilePhoto from '../../candidate/profile/section-details/CandidateProfilePhoto';
import { WORKFLOW_STEP_UNKNOWN } from '../../../constants/EmployerWorkflowStatus';
import Position from '../../../models/Position';
import { getLocationsTooltip } from '../../employer/position/header/JobHeaderUtils';
import Tooltip from '../../material-ui-kit/Tooltip/Tooltip';
import TextService from '../../../services/TextService';

const initialState = {
    data: [],
    loading: false,
    errorMessage: '',
    noResults: undefined,
    searchString: '',
};

// Modified by AI on 05/26/2026. Edit #1.
const CandidatesSearchComponent = (props) => {
    const { positionId } = props;
    const classes = useStyles();
    const { t } = useTranslation();

    const [state, setState] = useState(initialState);
    const debounceTimerRef = useRef(null);
    const lastSearchRef = useRef('');
    const requestIdRef = useRef(0);

    const { employerId } = useContext(EmployerContext);
    const { showErrorNotification } = useContext(NotificationContext);

    // Modified by AI on 07/13/2026. Edit #2.
    const performSearch = useCallback((term) => {
        requestIdRef.current += 1;
        const currentRequestId = requestIdRef.current;

        setState((currentState) => ({
            ...currentState, loading: true, errorMessage: '', noResults: false,
        }));

        EmployerHttpService.searchCandidates({ employerId, params: { term, positionId }, projection: ['employerId', 'talentId', 'candidateName', 'positionId'] })
            .then((data) => {
                if (currentRequestId === requestIdRef.current) {
                    setState((currentState) => ({
                        ...currentState,
                        data: _map(data, (candidate) => ({ value: getCandidateOptionHtml({ candidate, term }) })),
                        loading: false,
                        noResults: !get(data, 'length'),
                    }));
                }
            })
            .catch(() => {
                if (currentRequestId === requestIdRef.current) {
                    setState((currentState) => ({
                        ...currentState,
                        loading: false,
                        errorMessage: 'An application error occurred',
                    }));
                }
            });
    }, [employerId, positionId]);

    // Modified by AI on 07/13/2026. Edit #5.
    const onChange = (event) => {
        const searchString = get(event, 'target.value', '') || '';
        setState((currentState) => {
            const isDifferentSearchString = !searchString || searchString?.trim() !== currentState.searchString?.trim();
            return {
                ...currentState,
                searchString,
                ...(isDifferentSearchString && { data: [] }),
            };
        });

        // Debounced search
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }

        const trimmed = searchString?.replace(/\s+/, ' ').trim() || '';
        // If the input changes to a non-searchable value, invalidate any in-flight request
        // so stale responses don't repopulate results after clearing the input.
        if (trimmed.length < 2) {
            requestIdRef.current += 1;
            lastSearchRef.current = trimmed;
            setState((currentState) => ({
                ...currentState,
                loading: false,
                errorMessage: '',
                noResults: false,
            }));
            return;
        }
        if (trimmed !== lastSearchRef.current) {
            debounceTimerRef.current = setTimeout(() => {
                lastSearchRef.current = trimmed;
                performSearch(trimmed);
            }, 500);
        }
    };

    const updateState = (updatedState) => {
        setState((currentState) => ({
            ...currentState,
            ...updatedState,
        }));
    };

    const getCandidateOptionHtml = ({ candidate, term }) => <Link
        href={`/employer/${candidate.employerId}/position/${candidate.positionId}/workflowStep/${WORKFLOW_STEP_UNKNOWN}/candidates/${candidate.talentId}`}
        target='_self'
        underline='none'
    >
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: 16, display: 'flex' }}>
                <CandidateProfilePhoto
                    photoUrl={candidate.photoUrl}
                    width={32}
                    height={32}
                />
            </span>
            <div style={{ display: 'flex', flexFlow: 'column' }}>
                <span style={{ fontSize: 15, color: darkslategray }}>
                    <Highlighter
                        highlightClassName="highlighted-text"
                        searchWords={[term]}
                        autoEscape={true}
                        textToHighlight={candidate.candidateName}
                    />
                </span>
                <span style={{ fontSize: 13, color: lightslategray }}>
                    {candidate.jobTitle}
                </span>

                <Tooltip
                    placement={'bottom-start'}
                    title={getLocationsTooltip({ locations: candidate.jobLocations })}
                >
                    <span
                        style={{ fontSize: 13, color: lightslategray }}
                        dangerouslySetInnerHTML={{
                            __html: TextService.sanitize(
                                Position.getFormattedPositionInfo(
                                    { locations: candidate.jobLocations },
                                    t,
                                    true,
                                ),
                            ),
                        }}
                    />
                </Tooltip>
            </div>
        </div>
    </Link>;

    useEffect(() => {
        if (state.errorMessage) {
            showErrorNotification();
            setState((currentState) => ({
                ...currentState,
                errorMessage: '',
            }));
        }
    }, [state.errorMessage]);

    // Modified by AI on 07/13/2026. Edit #3.
    useEffect(() => {
        requestIdRef.current += 1;
        lastSearchRef.current = '';
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
        setState(initialState);
    }, [employerId]);

    // Modified by AI on 07/13/2026. Edit #4.
    // Cleanup debounce timer and invalidate in-flight requests on unmount
    useEffect(() => () => {
        requestIdRef.current += 1;
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
    }, []);

    // Modified by AI on 07/16/2026. Edit #8.
    const resetSearch = () => {
        setState((currentState) => ({
            ...currentState,
            data: [],
            searchString: '',
            loading: false,
            noResults: false,
        }));
        requestIdRef.current += 1;
        lastSearchRef.current = '';
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
    };

    const loader = <Loader
        color={saffronMango}
        width={30}
        height={30}
        transparentBackground
        className={classes.loader}
    />;

    return <Fragment>
        <div className={classes.inputWrapper}>
            <i className={classes.candidatesSearchIcon}/>
            <input
                data-test-id='inter-job-title-input'
                id='candidate-search-job-title-input'
                placeholder={t('CANDIDATES_SEARCH_LEAD_TEXT')}
                value={state.searchString}
                className={classes.filterInput}
                onChange={onChange}
            />
            {state.searchString && !state.loading && (
                <FontAwesomeIcon
                    className={classes.clearIcon}
                    icon='times'
                    size={'xs'}
                    data-test-id='tag-remove-button'
                    onFocus={(e) => e.stopPropagation()}
                    onClick={resetSearch}
                />
            )}
            { state.loading && loader }
            <SuggestionOptions
                classNames={classes.suggestions}
                suggestions={state.data}
                isOpen={state.data.length || state.noResults === true}
                isFetching={state.loading}
                isEmptyFetchResult={state.noResults}
            />
        </div>
    </Fragment>;
};

CandidatesSearchComponent.propTypes = {};

export default memo(CandidatesSearchComponent);
